import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    ElementRef,
    HostBinding,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    ReflectiveInjector,
    ResolvedReflectiveProvider,
    ViewChild,
    ViewContainerRef
} from "@angular/core";
import {BehaviorSubject, Subject, Subscription} from "rxjs";
import {NavigationService} from "../navigation.service";
import {Page, PageComponentContainer, PageSpec} from "../page";
import {OrchestrationEvents} from "../orchestration-events";
import {LoadingStatus} from "../../common-types/loading-status";

@Component({
    selector: "woa-page-container",
    templateUrl: "./page-container.component.html"
})
export class PageContainerComponent implements OnInit, OnDestroy {
    @Input() rootSelector: string;

    @HostBinding("class.hidden") get hidden(): boolean {
        return OrchestrationEvents.initialLoadStatus$.value === LoadingStatus.LOADING;
    }

    @ViewChild("content", {read: ViewContainerRef}) viewContainerRef: ViewContainerRef;

    open$: Subject<boolean> = new BehaviorSubject<boolean>(false);

    private subscriptions: Subscription = new Subscription();

    constructor(private element: ElementRef,
                private navigationService: NavigationService,
                private injector: Injector,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    async ngOnInit(): Promise<void> {
        this.subscriptions.add(this.navigationService.pageCreated$.subscribe((pageSpec: PageSpec) => {

            // Create a container component for each page component.
            // This is required to allow the swipable behaviour to be attached.
            // Inject the page spec into the container. This gives the container access to the page instance and the specified geometry.
            const providers: ResolvedReflectiveProvider[] = ReflectiveInjector.resolve([{
                provide: PageSpec,
                useValue: pageSpec
            }]);
            const injector: ReflectiveInjector = ReflectiveInjector.fromResolvedProviders(providers, this.injector);
            const containerComponentRef: ComponentRef<PageComponentContainer> = this.navigationService.getComponentFactory("woa-page-component-container").create(injector);

            // Inject the container component ref into the page instance.
            // We use this to close the container component based on dismissal of the page instance.
            // It also gives the page access to the page spec including the geometry (e.g. the nav menu places its close button based on the side of the page container).
            pageSpec.componentRef.instance.containerComponentRef = containerComponentRef;

            // Add the container component to this container
            this.viewContainerRef.insert(containerComponentRef.hostView);
            containerComponentRef.location.nativeElement.style.zIndex = this.viewContainerRef.length;

            // Start the view open transition after it has been initialised
            setTimeout(() => {
                this.open$.next(true);
                containerComponentRef.instance.openPageComponent();
                this.changeDetectorRef.detectChanges();
            });
        }));

        this.subscriptions.add(this.navigationService.pageDismissed$.subscribe((pageComponentRef: ComponentRef<Page>) => {
            const containerComponentRef: ComponentRef<PageComponentContainer> = pageComponentRef.instance.containerComponentRef;
            if (!containerComponentRef) {
                return;
            }

            // Close the page container if removing the last page component (start the close transition)
            if (this.viewContainerRef.length == 1) {
                this.open$.next(false);
            }

            // Close the component container (start the close transition)
            containerComponentRef.instance.closePageComponent();
            this.changeDetectorRef.detectChanges();

            // Destroy the component container AFTER its close transition has had time to complete
            setTimeout(() => {
                containerComponentRef.destroy();
            }, this.transitionPeriod * 2);
        }));

        // Create the initial root page
        if (this.rootSelector) {
            return this.navigationService.createPage(this.rootSelector, undefined, undefined, true);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get transitionPeriod(): number {
        const period: string = getComputedStyle(this.element.nativeElement).getPropertyValue("--woa-page-transition-period").trim();
        return period.endsWith("ms") ? parseInt(period.replace("ms", ""))
            : period.endsWith("s") ? parseInt(period.replace("s", "")) / 1000
                : 300;
    }

    protected readonly OrchestrationEvents = OrchestrationEvents;
    protected readonly LoadingStatus = LoadingStatus;
}



// WEBPACK FOOTER //
// ./src/app/orchestration/page-container/page-container.component.ts